import type { AdobeGateway } from './AdobeGateway';
import { BasicAdobePlaybackHelper } from './BasicAdobePlaybackHelper';
import {
  type UnifiedTrackingMachineAdBeginEvent,
  type UnifiedTrackingMachineAdBreakBeginEvent,
  UnifiedTrackingMachineLoadEvent,
  type UnifiedTrackingMachineTimeUpdateEvent,
} from '../tracking/UnifiedTrackingMachine';
import { getMediaObject, MediaHeartbeatEvents } from './AdobeTrackingMachine';
import { createAdObject } from '../../services/Adobe/functions/createAdObject';
import { createAdBreakObject } from '../../services/Adobe/functions/createAdBreakObject';
import type { TimeTableEntry } from './TimeTable';
import type { AdobeProgramTrackingFragment } from '../dataProviders/__generated__/Tracking';
import type { AdobeTrackingStrategy } from './AdobeTrackingStrategy';

import type { AdobeMetadata } from './types';

export class AdobeVodTrackingStrategy implements AdobeTrackingStrategy {
  private gateway: AdobeGateway;
  private playerName: string;
  private playbackHelper: BasicAdobePlaybackHelper;

  constructor(adobeGateway: AdobeGateway, playerName: string) {
    this.gateway = adobeGateway;
    this.playerName = playerName;
    this.playbackHelper = new BasicAdobePlaybackHelper(adobeGateway);
  }

  onLoad(event: UnifiedTrackingMachineLoadEvent): void {
    const { media } = event.value;
    const metadata = this.getMetadata(event);
    const adobe = media.playback!.tracking!.adobe!;
    this.gateway.trackSessionStart(getMediaObject(adobe, media), metadata);
    this.playbackHelper.isInSession = true;
  }

  onAdEnd(): void {
    this.gateway.trackEvent(MediaHeartbeatEvents.adComplete);
  }

  onAdBegin(event: UnifiedTrackingMachineAdBeginEvent): void {
    this.gateway.trackEvent(
      MediaHeartbeatEvents.adStart,
      createAdObject(
        event.value.creativeId!,
        event.value.creativeId!,
        event.value.sequence,
        event.value.duration,
        this.playerName
      )
    );
  }

  onAdBreakEnd(): void {
    this.gateway.trackEvent(MediaHeartbeatEvents.adBreakComplete);
  }

  onAdBreakBegin(event: UnifiedTrackingMachineAdBreakBeginEvent): void {
    // On VOD we want to set a playhead to 0
    if (this.playbackHelper.lastPlayhead > 0) {
      this.playbackHelper.updatePlayhead(0);
    }

    this.gateway.trackEvent(
      MediaHeartbeatEvents.adBreakStart,
      createAdBreakObject('pre-roll', 1, 0)
    );
  }

  onTrackingEntries(_entries: TimeTableEntry<AdobeProgramTrackingFragment>[]): void {}

  onTimeUpdate(event: UnifiedTrackingMachineTimeUpdateEvent): void {
    this.playbackHelper.updatePlayhead(event.value.currentTime);
    this.playbackHelper.sendQualityOfExperience(event.value);
  }

  onSeeking(): void {
    this.playbackHelper.onSeeking();
  }

  onDestroy(): void {
    this.playbackHelper.onDestroy();
  }

  onBuffering(): void {
    this.playbackHelper.onBuffering();
  }

  onPause(): void {
    this.playbackHelper.onPause();
  }

  onStop(): void {
    this.playbackHelper.onStop();
  }

  private getMetadata(event: UnifiedTrackingMachineLoadEvent): AdobeMetadata {
    const metadata = this.playbackHelper.getBasicMetadata(event);

    return {
      ...metadata,
    } as AdobeMetadata;
  }
}
